import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Button, TextField, Stack, CircularProgress } from '@mui/material';
import { Helmet }         from 'react-helmet-async';
import EditIcon           from '@mui/icons-material/Edit';
import axiosPublic        from '../api/axiosPublic';
import axiosInstance      from '../api/axiosConfig';
import { useAuth }        from '../context/AuthContext';

const WALNUT     = '#2C1F14';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const TERRACOTTA = '#C4622D';

export default function Contact() {
    const { user } = useAuth();
    const canEdit  = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const [content,  setContent]  = useState('');
    const [loading,  setLoading]  = useState(true);
    const [editing,  setEditing]  = useState(false);
    const [draft,    setDraft]    = useState('');
    const [saving,   setSaving]   = useState(false);

    useEffect(() => {
        axiosPublic.get('/api/info')
            .then(res => setContent(res.data?.contact || ''))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axiosInstance.put('/api/info', { contact: draft });
            setContent(res.data?.contact || '');
            setEditing(false);
        } catch {}
        finally { setSaving(false); }
    };

    return (
        <Box sx={{ bgcolor: PARCHMENT, minHeight: '100vh' }}>
            <Helmet><title>Contact — HayTour</title></Helmet>

            <Box sx={{ bgcolor: WALNUT, py: 5, px: { xs: 2, md: 6 } }}>
                <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.22em', fontWeight: 700 }}>
                    Get in touch
                </Typography>
                <Typography variant="h4" fontWeight={800}
                    sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif", mt: 0.5 }}>
                    Contact
                </Typography>
            </Box>

            <Container maxWidth="md" sx={{ py: 6 }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid rgba(196,98,45,0.12)' }}>
                    {canEdit && !editing && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button startIcon={<EditIcon />} size="small" variant="outlined"
                                onClick={() => { setDraft(content); setEditing(true); }}
                                sx={{ color: TERRACOTTA, borderColor: 'rgba(196,98,45,0.4)', borderRadius: 2 }}>
                                Edit
                            </Button>
                        </Box>
                    )}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} sx={{ color: TERRACOTTA }} />
                        </Box>
                    ) : editing ? (
                        <Stack spacing={2}>
                            <TextField multiline fullWidth minRows={6} value={draft}
                                onChange={e => setDraft(e.target.value)} autoFocus />
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                                <Button variant="contained" onClick={handleSave} disabled={saving}
                                    sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2 }}>
                                    {saving ? 'Saving…' : 'Save'}
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography variant="body1" color={content ? WALNUT : 'text.secondary'}
                            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                            {content || 'Content coming soon.'}
                        </Typography>
                    )}
                </Paper>
            </Container>
        </Box>
    );
}
